import { fetchEventSource } from "@microsoft/fetch-event-source";
import { CHAT_API_BASE } from "./appServices";
import { PrepareStreamResponse } from "./messageService";
import DocumentReference from "@/models/conversation/document";

export type ChatChunkEvent = {
  text: string;
  references: DocumentReference[];
  chainOfThought: boolean;
  rationale: string;
  statusShort: string;
  statusLong: string;
  lemmatizedText: string;
};

export type ChatDoneEvent = {
  text: string;
  references: DocumentReference[];
  chainOfThoughtMessage: string;
  revisedPrompt: string;
  lemmatizedText: Record<string, string>;
  statusShort: string;
  statusLong: string;
};

export type ChatTitleEvent = {
  title: string;
};

export type ChatStreamCallbacks = {
  onTitle: (title: string) => void | Promise<void>;
  onChunk: (chunk: ChatChunkEvent) => void | Promise<void>;
  onDone: (result: ChatDoneEvent) => void | Promise<void>;
  onError: (error: string) => void | Promise<void>;
};

export type ChatStreamSubscribeOptions = {
  generationId: string;
  afterSeq?: number;
  callbacks: ChatStreamCallbacks;
  token: string;
  onSeq?: (seq: number) => void;
};

const parseSeq = (eventId: string | undefined): number | null => {
  if (!eventId) return null;
  const seq = Number.parseInt(eventId, 10);
  return Number.isNaN(seq) ? null : seq;
};

const parseStreamPayload = <T>(data: string): T | null => {
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
};

const handleStreamEvent = async (
  event: { event?: string; data: string; id?: string },
  callbacks: ChatStreamCallbacks,
  onSeq?: (seq: number) => void
): Promise<"done" | "error" | "continue"> => {
  const seq = parseSeq(event.id);
  if (seq !== null) {
    onSeq?.(seq);
  }

  if (event.event === "title") {
    const payload = parseStreamPayload<ChatTitleEvent>(event.data);
    if (!payload) {
      await callbacks.onError("Ukendt streamingfejl");
      return "error";
    }
    await callbacks.onTitle(payload.title);
    return "continue";
  }

  if (event.event === "chunk") {
    const payload = parseStreamPayload<ChatChunkEvent>(event.data);
    if (!payload) {
      await callbacks.onError("Ukendt streamingfejl");
      return "error";
    }
    await callbacks.onChunk(payload);
    return "continue";
  }

  if (event.event === "done") {
    const payload = parseStreamPayload<ChatDoneEvent>(event.data);
    if (!payload) {
      await callbacks.onError("Ukendt streamingfejl");
      return "error";
    }
    await callbacks.onDone(payload);
    return "done";
  }

  if (event.event === "error") {
    const payload = parseStreamPayload<{ message?: string }>(event.data);
    await callbacks.onError(payload?.message ?? "Ukendt streamingfejl");
    return "error";
  }

  return "continue";
};

export const startChatGeneration = async (
  request: PrepareStreamResponse & { sessionId: string },
  token: string
): Promise<string> => {
  const response = await fetch(`${CHAT_API_BASE}/chat/stream/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...request,
      sessionId: request.sessionId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to start chat stream (${response.status})`);
  }

  const payload = (await response.json()) as { generationId: string };
  return payload.generationId;
};

export type PendingStreamGeneration = {
  generationId: string;
  conversationId: string;
  status: string;
  updatedAt: string;
};

export const listPendingGenerations = async (
  token: string
): Promise<PendingStreamGeneration[]> => {
  const response = await fetch(`${CHAT_API_BASE}/chat/stream/pending`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to list pending streams (${response.status})`);
  }

  const payload = (await response.json()) as {
    generations?: PendingStreamGeneration[];
  };

  return payload.generations ?? [];
};

export const ackChatGeneration = async (
  generationId: string,
  token: string
): Promise<void> => {
  const response = await fetch(
    `${CHAT_API_BASE}/chat/stream/${encodeURIComponent(generationId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.status === 404) {
    return;
  }

  if (!response.ok) {
    throw new Error(`Failed to ack chat stream (${response.status})`);
  }
};

export const subscribeChatGeneration = ({
  generationId,
  afterSeq = 0,
  callbacks,
  token,
  onSeq,
}: ChatStreamSubscribeOptions): AbortController => {
  const controller = new AbortController();
  const url = `${CHAT_API_BASE}/chat/stream/${encodeURIComponent(generationId)}/events?afterSeq=${afterSeq}`;

  void fetchEventSource(url, {
    method: "GET",
    openWhenHidden: true,
    signal: controller.signal,
    headers: {
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    async onopen(response) {
      if (response.ok) {
        return;
      }

      const errorMessage =
        response.status === 404
          ? "Stream generation not found (404)"
          : `HTTP ${response.status}`;

      await callbacks.onError(errorMessage);
      controller.abort();
      return;
    },
    onmessage: async (event) => {
      if (!event.data) {
        return;
      }

      try {
        const result = await handleStreamEvent(event, callbacks, onSeq);
        if (result === "done" || result === "error") {
          controller.abort();
        }
      } catch (error) {
        await callbacks.onError(
          error instanceof Error ? error.message : "Ukendt streamingfejl"
        );
        controller.abort();
      }
    },
    onerror: (error) => {
      void callbacks.onError(
        error instanceof Error ? error.message : "Ukendt streamingfejl"
      );
      controller.abort();
    },
  });

  return controller;
};

export const streamChat = async (
  request: PrepareStreamResponse & { sessionId: string },
  callbacks: ChatStreamCallbacks,
  token: string,
  options?: { onSeq?: (seq: number) => void }
): Promise<{ controller: AbortController; generationId: string }> => {
  const generationId = await startChatGeneration(request, token);
  const controller = subscribeChatGeneration({
    generationId,
    afterSeq: 0,
    callbacks,
    token,
    onSeq: options?.onSeq,
  });
  return { controller, generationId };
};
